import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Upload, FileText, X, Trash2, Search, Loader2, ChevronDown, ChevronRight, Eye, ExternalLink, ArrowLeft, Package, Users, FolderOpen } from 'lucide-react';
import { db, storage } from '../firebase';
import { fetchQuotesForOrders } from '../utils/quotesService';

// Cache for orders and PIs
const ordersCache = new Map();
const pisCache = new Map();
const cacheExpiry = 2 * 60 * 1000; // 2 minutes (reduced for faster updates)

// Utility function to remove .pdf extension from filename
const removePdfExtension = (filename) => {
  if (!filename) return 'proforma';
  return filename.replace(/\.pdf$/i, '');
};

export default function ProformaManager() {
  const [currentView, setCurrentView] = useState('vendor-list'); // 'vendor-list', 'vendor-pis', 'pi-orders', 'order-detail'
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedPI, setSelectedPI] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [piToDelete, setPiToDelete] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [vendorPIs, setVendorPIs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPIs, setLoadingPIs] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [search, setSearch] = useState('');
  const [allOrderQuotes, setAllOrderQuotes] = useState({});
  const [refresh, setRefresh] = useState(false);

  // Upload functionality state
  const [allOrders, setAllOrders] = useState([]);
  const [allOrderQuotesForUpload, setAllOrderQuotesForUpload] = useState({});
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [showUploadSection, setShowUploadSection] = useState(false);
  
  // Enhanced search state - store all orders and their quotes for comprehensive search
  const [allOrdersForSearch, setAllOrdersForSearch] = useState([]);
  const [allOrderQuotesForSearch, setAllOrderQuotesForSearch] = useState({});
  
  // Upload section search state
  const [uploadSearchQuery, setUploadSearchQuery] = useState('');

  // Fetch all vendors with their PI counts - OPTIMIZED
  const fetchVendors = useCallback(async () => {
    const cacheKey = 'vendors_with_pis';
    const cached = pisCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < cacheExpiry) {
      setVendorList(cached.data);
      setLoading(false);
      return cached.data;
    }

    try {
      setLoading(true);
      
      // Use a more efficient approach - get all quotes with PIs in one query
      const allQuotesWithPIs = [];
      
      // Get all orders first
      const ordersSnap = await getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc')));
      const orderIds = ordersSnap.docs.map(doc => doc.id);
      
      // Process orders in batches to avoid overwhelming Firestore
      const batchSize = 10;
      const totalBatches = Math.ceil(orderIds.length / batchSize);
      setLoadingProgress({ current: 0, total: totalBatches });
      
      for (let i = 0; i < orderIds.length; i += batchSize) {
        const batch = orderIds.slice(i, i + batchSize);
        const currentBatch = Math.floor(i / batchSize) + 1;
        
        // Update progress
        setLoadingProgress({ current: currentBatch, total: totalBatches });
        
        // Process batch in parallel
        const batchPromises = batch.map(async (orderId) => {
          try {
            const quotesSnap = await getDocs(collection(db, `orders/${orderId}/quotes`));
            const orderData = ordersSnap.docs.find(doc => doc.id === orderId)?.data();
            
            return quotesSnap.docs
              .map(quoteDoc => ({
                ...quoteDoc.data(),
                quoteId: quoteDoc.id,
                orderId: orderId,
                orderTitle: orderData?.orderTitle || 'Unknown Order'
              }))
              .filter(quote => quote.proformaInvoiceUrl);
          } catch (error) {
            console.warn(`Failed to fetch quotes for order ${orderId}:`, error);
            return [];
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        allQuotesWithPIs.push(...batchResults.flat());
      }

      // Group by vendor
      const vendorMap = new Map();
      
      allQuotesWithPIs.forEach(quote => {
        const supplier = quote.supplier || 'Unknown Supplier';
        
        if (!vendorMap.has(supplier)) {
          vendorMap.set(supplier, {
            name: supplier,
            piCount: 0,
            totalOrders: 0,
            latestPI: null,
            pis: new Map() // Store PIs grouped by filename
          });
        }
        
        const vendor = vendorMap.get(supplier);
        const piName = removePdfExtension(quote.proformaInvoiceFilename || 'proforma.pdf');
        
        // Count unique PIs (group by filename)
        if (!vendor.pis.has(piName)) {
          vendor.pis.set(piName, {
            piName: piName,
            uploadDate: quote.createdAt,
            price: quote.price,
            currency: quote.currency || 'INR',
            status: quote.status,
            orderCount: 0,
            orders: [], // Store order references
            proformaInvoiceUrl: quote.proformaInvoiceUrl,
            supplier: supplier
          });
          vendor.piCount += 1;
        }
        
        // Update PI order count and add order reference
        const pi = vendor.pis.get(piName);
        pi.orderCount += 1;
        pi.orders.push({
          orderId: quote.orderId,
          quoteId: quote.quoteId,
          orderTitle: quote.orderTitle
        });
        vendor.totalOrders += 1;
        
        // Track latest PI date
        const piDate = quote.createdAt;
        if (!vendor.latestPI || (piDate && piDate.toDate() > vendor.latestPI.toDate())) {
          vendor.latestPI = piDate;
        }
      });

      const vendors = Array.from(vendorMap.values()).map(vendor => ({
        name: vendor.name,
        piCount: vendor.piCount,
        totalOrders: vendor.totalOrders,
        latestPI: vendor.latestPI,
        pis: Array.from(vendor.pis.values()) // Store PIs for quick access
      })).sort((a, b) => {
        const dateA = a.latestPI?.toDate() || new Date(0);
        const dateB = b.latestPI?.toDate() || new Date(0);
        return dateB - dateA;
      });

      pisCache.set(cacheKey, {
        data: vendors,
        timestamp: Date.now()
      });
      
      setVendorList(vendors);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch PIs for a specific vendor - OPTIMIZED (use cached data)
  const fetchVendorPIs = useCallback(async (vendorName) => {
    setLoadingPIs(true);
    try {
      // First try to get from cached vendor data
      const cachedVendor = vendorList.find(v => v.name === vendorName);
      if (cachedVendor && cachedVendor.pis) {
        // Use cached PI data with order information
        const vendorPIs = cachedVendor.pis.map(pi => ({
          id: `${vendorName}-${pi.piName}`,
          piName: pi.piName,
          supplier: vendorName,
          uploadDate: pi.uploadDate,
          price: pi.price,
          currency: pi.currency,
          status: pi.status,
          orderCount: pi.orderCount,
          orders: pi.orders || [], // Use stored order data
          proformaInvoiceUrl: pi.proformaInvoiceUrl
        })).sort((a, b) => {
          const dateA = a.uploadDate?.toDate?.() || new Date(a.uploadDate) || new Date(0);
          const dateB = b.uploadDate?.toDate?.() || new Date(b.uploadDate) || new Date(0);
          return dateB - dateA;
        });

        setVendorPIs(vendorPIs);
        setLoadingPIs(false);
          return;
        }

      // Fallback: fetch from database if not in cache
      const ordersSnap = await getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc')));
      const vendorPIs = [];
      
      for (const orderDoc of ordersSnap.docs) {
        const quotesSnap = await getDocs(collection(db, `orders/${orderDoc.id}/quotes`));
        
        quotesSnap.forEach(quoteDoc => {
          const quoteData = quoteDoc.data();
          if (quoteData.proformaInvoiceUrl && quoteData.supplier === vendorName) {
            const piName = removePdfExtension(quoteData.proformaInvoiceFilename || 'proforma.pdf');
            
            // Check if this PI already exists (group by filename)
            const existingPI = vendorPIs.find(pi => pi.piName === piName);
            
            if (existingPI) {
              // Add this order to existing PI
              existingPI.orders.push({
                orderId: orderDoc.id,
                quoteId: quoteDoc.id,
                orderTitle: orderDoc.data().orderTitle || 'Unknown Order'
              });
              existingPI.orderCount = existingPI.orders.length;
            } else {
              // Create new PI entry
              vendorPIs.push({
                id: `${vendorName}-${piName}`,
                piName: piName,
                supplier: vendorName,
                uploadDate: quoteData.createdAt,
                proformaInvoiceUrl: quoteData.proformaInvoiceUrl,
                price: quoteData.price,
                currency: quoteData.currency || 'INR',
                status: quoteData.status,
                orders: [{
                  orderId: orderDoc.id,
                  quoteId: quoteDoc.id,
                  orderTitle: orderDoc.data().orderTitle || 'Unknown Order'
                }],
                orderCount: 1
              });
            }
          }
        });
      }

      // Sort by upload date (newest first)
      vendorPIs.sort((a, b) => {
        const dateA = a.uploadDate?.toDate?.() || new Date(a.uploadDate) || new Date(0);
        const dateB = b.uploadDate?.toDate?.() || new Date(b.uploadDate) || new Date(0);
        return dateB - dateA;
      });

      setVendorPIs(vendorPIs);
    } catch (error) {
      console.error('Error fetching vendor PIs:', error);
    } finally {
      setLoadingPIs(false);
    }
  }, [vendorList]);

  // Fetch orders for a specific PI
  const fetchOrdersForPI = useCallback(async (pi) => {
    setLoadingOrders(true);
    try {
      const relatedOrders = [];
      
      // If PI has orders array, use it directly
      if (pi.orders && pi.orders.length > 0) {
        // Get all orders from the PI's order list
        for (const orderRef of pi.orders) {
          const orderSnap = await getDocs(query(collection(db, 'orders')));
          orderSnap.forEach(orderDoc => {
            if (orderDoc.id === orderRef.orderId) {
              relatedOrders.push({
                id: orderDoc.id,
                ...orderDoc.data()
              });
            }
          });
        }
      } else {
        // Fallback: search for orders that have this PI
        const ordersSnap = await getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc')));
        
        for (const orderDoc of ordersSnap.docs) {
          const quotesSnap = await getDocs(collection(db, `orders/${orderDoc.id}/quotes`));
          
          quotesSnap.forEach(quoteDoc => {
            const quoteData = quoteDoc.data();
            if (quoteData.proformaInvoiceUrl && 
                quoteData.supplier === pi.supplier && 
                removePdfExtension(quoteData.proformaInvoiceFilename || 'proforma.pdf') === pi.piName) {
              
              // Check if this order is already added
              if (!relatedOrders.find(o => o.id === orderDoc.id)) {
                relatedOrders.push({
                  id: orderDoc.id,
                  ...orderDoc.data()
                });
              }
            }
          });
        }
      }

      setOrders(relatedOrders);
      
      // Fetch quotes for these orders
      if (relatedOrders.length > 0) {
        const quotesData = await fetchQuotesForOrders(relatedOrders.map(o => o.id));
        setAllOrderQuotes(quotesData);
      }
    } catch (error) {
      console.error('Error fetching PI orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  // Fetch all orders for upload functionality
  const fetchAllOrdersForUpload = useCallback(async () => {
    try {
      const ordersSnap = await getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc')));
      const allOrdersData = ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllOrders(allOrdersData);
      
      // Fetch quotes for all orders
      const quotesData = await fetchQuotesForOrders(allOrdersData.map(o => o.id));
      setAllOrderQuotesForUpload(quotesData);
    } catch (error) {
      console.error('Error fetching orders for upload:', error);
    }
  }, []);

  // Fetch all orders and quotes for enhanced search functionality
  const fetchAllOrdersForSearch = useCallback(async () => {
    try {
      const ordersSnap = await getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc')));
      const allOrdersData = ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllOrdersForSearch(allOrdersData);
      
      // Fetch quotes for all orders
      const quotesData = await fetchQuotesForOrders(allOrdersData.map(o => o.id));
      setAllOrderQuotesForSearch(quotesData);
    } catch (error) {
      console.error('Error fetching orders for search:', error);
    }
  }, []);

  // Enhanced filter vendors based on search - includes orders and PIs
  const filteredVendors = useMemo(() => {
    if (!search) return vendorList;
    
    const searchLower = search.toLowerCase();
    
    return vendorList.filter(vendor => {
      // Direct vendor name match
      if (vendor.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Check if any orders with PIs from this vendor match the search
      const vendorOrders = allOrdersForSearch.filter(order => {
        const orderQuotes = allOrderQuotesForSearch[order.id] || [];
        const hasVendorQuote = orderQuotes.some(quote => 
          quote.supplier && quote.supplier.toLowerCase().includes(vendor.name.toLowerCase())
        );
        
        if (!hasVendorQuote) return false;
        
        // Check if order matches search criteria
        return (
          (order.orderTitle && order.orderTitle.toLowerCase().includes(searchLower)) ||
          (order.description && order.description.toLowerCase().includes(searchLower)) ||
          (order.serialNumber && order.serialNumber.toLowerCase().includes(searchLower))
        );
      });
      
      return vendorOrders.length > 0;
    });
  }, [vendorList, search, allOrdersForSearch, allOrderQuotesForSearch]);

  // Filter vendor PIs based on search
  const filteredVendorPIs = useMemo(() => {
    if (!search) return vendorPIs;
    return vendorPIs.filter(pi => 
      removePdfExtension(pi.piName).toLowerCase().includes(search.toLowerCase())
    );
  }, [vendorPIs, search]);

  // Handle vendor selection
  const handleVendorClick = (vendor) => {
    setSelectedVendor(vendor);
    setCurrentView('vendor-pis');
    fetchVendorPIs(vendor.name);
  };

  // Handle PI selection
  const handlePIClick = (pi) => {
    setSelectedPI(pi);
    setCurrentView('pi-orders');
    fetchOrdersForPI(pi);
  };

  // Navigation handlers
  const handleBackToVendors = () => {
    setCurrentView('vendor-list');
    setSelectedVendor(null);
    setVendorPIs([]);
  };

  const handleBackToVendorPIs = () => {
    setCurrentView('vendor-pis');
    setSelectedPI(null);
    setOrders([]);
    setAllOrderQuotes({});
  };

  const handleBackToPIOrders = () => {
    setCurrentView('pi-orders');
    setSelectedOrder(null);
  };

  // Handle order card click
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setCurrentView('order-detail');
  };

  // Handle delete confirmation
  const handleDeleteClick = (pi) => {
    setPiToDelete(pi);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (piToDelete) {
      handleDeletePI(piToDelete);
      setShowDeleteModal(false);
      setPiToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
      setPiToDelete(null);
  };

  // Upload functionality
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      } else {
      alert("Please select a PDF file.");
    }
  };

  const handleQuoteSelect = (orderId, quoteId) => {
    const quoteKey = `${orderId}:${quoteId}`;
    setSelectedQuotes(prev => 
      prev.includes(quoteKey) 
        ? prev.filter(q => q !== quoteKey)
        : [...prev, quoteKey]
    );
  };

  const handleUpload = async () => {
    if (!file || selectedQuotes.length === 0) return;
    setUploading(true);
    
    try {
      const originalFilename = file.name;
      
      for (const quoteKey of selectedQuotes) {
        const [orderId, quoteId] = quoteKey.split(':');
        
        const piRef = storageRef(storage, `orders/${orderId}/quotes/${quoteId}/${originalFilename}`);
        await uploadBytes(piRef, file);
        const url = await getDownloadURL(piRef);
        
        const quoteDoc = doc(db, `orders/${orderId}/quotes`, quoteId);
        await updateDoc(quoteDoc, { 
          proformaInvoiceUrl: url,
          proformaInvoiceFilename: originalFilename
        });
      }
      
      // Clear cache and refresh
      pisCache.clear();
      setRefresh(prev => !prev);
      setFile(null);
      setSelectedQuotes([]);
      setShowUploadSection(false);
      alert('PI uploaded successfully');
    } catch (error) {
      console.error('Error uploading PI:', error);
      alert('Failed to upload PI. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Delete PI function
  const handleDeletePI = async (pi) => {
    if (!window.confirm(`Are you sure you want to delete the PI "${removePdfExtension(pi.piName)}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Delete PI from all associated orders
      for (const orderRef of pi.orders) {
        const filename = removePdfExtension(pi.piName);
        const piRef = storageRef(storage, `orders/${orderRef.orderId}/quotes/${orderRef.quoteId}/${filename}`);
        
        try {
          await deleteObject(piRef);
        } catch (error) {
          console.warn(`Failed to delete file for order ${orderRef.orderId}:`, error);
        }

        const quoteRef = doc(db, `orders/${orderRef.orderId}/quotes`, orderRef.quoteId);
        await updateDoc(quoteRef, {
          proformaInvoiceUrl: null,
          proformaInvoiceFilename: null
        });
      }

      // Clear cache and refresh
      pisCache.clear();
      setRefresh(prev => !prev);
      
      alert('PI deleted successfully');
    } catch (error) {
      console.error('Error deleting PI:', error);
      alert('Failed to delete PI. Please try again.');
    }
  };

  // Format functions
  const formatDate = (date) => {
    if (!date) return 'Unknown';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  };

  const formatPrice = (price, currency = 'INR') => {
    if (!price) return 'N/A';
    return `${currency} ${price.toLocaleString()}`;
  };

  useEffect(() => {
    fetchVendors();
    fetchAllOrdersForSearch(); // Fetch orders for enhanced search
  }, [fetchVendors, fetchAllOrdersForSearch, refresh]);

  useEffect(() => {
    if (showUploadSection) {
      fetchAllOrdersForUpload();
    } else {
      // Clear upload search when upload section is closed
      setUploadSearchQuery('');
    }
  }, [showUploadSection, fetchAllOrdersForUpload]);

  // Render Vendor List View
  const renderVendorListView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText size={22} /> Proforma Invoice Manager
      </h1>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
            />
          </div>
          <button
            onClick={() => setShowUploadSection(!showUploadSection)}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <Upload size={16} />
            Upload PI
          </button>
        </div>
      </div>

      {/* Upload Section */}
      {showUploadSection && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold mb-4">Upload New Proforma Invoice</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF File
              </label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            {file && (
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  Selected file: <span className="font-medium">{file.name}</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Quotes to Attach PI
                  </label>
                  
                  {/* Dedicated search bar for upload section */}
                  <div className="mb-3">
                    <input
                      type="text"
                      value={uploadSearchQuery}
                      onChange={(e) => setUploadSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Search orders by title, description, RN number, or supplier..."
                    />
                  </div>
                  
                  {/* Show search results count */}
                  {uploadSearchQuery && (
                    <div className="text-sm text-gray-600 mb-2">
                      {allOrders.filter(order => {
                        const searchLower = uploadSearchQuery.toLowerCase();
                        const orderMatches = (
                          (order.orderTitle && order.orderTitle.toLowerCase().includes(searchLower)) ||
                          (order.description && order.description.toLowerCase().includes(searchLower)) ||
                          (order.serialNumber && order.serialNumber.toLowerCase().includes(searchLower))
                        );
                        const orderQuotes = allOrderQuotesForUpload[order.id] || [];
                        const quoteMatches = orderQuotes.some(quote => 
                          (quote.supplier && quote.supplier.toLowerCase().includes(searchLower)) ||
                          (quote.status && quote.status.toLowerCase().includes(searchLower))
                        );
                        return orderMatches || quoteMatches;
                      }).length} order(s) found
                    </div>
                  )}
                  
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded">
                    {allOrders.filter(order => {
                      if (!uploadSearchQuery) return true;
                      const searchLower = uploadSearchQuery.toLowerCase();
                      
                      // Check order fields
                      const orderMatches = (
                        (order.orderTitle && order.orderTitle.toLowerCase().includes(searchLower)) ||
                        (order.description && order.description.toLowerCase().includes(searchLower)) ||
                        (order.serialNumber && order.serialNumber.toLowerCase().includes(searchLower))
                      );
                      
                      // Check if any quotes match
                      const orderQuotes = allOrderQuotesForUpload[order.id] || [];
                      const quoteMatches = orderQuotes.some(quote => 
                        (quote.supplier && quote.supplier.toLowerCase().includes(searchLower)) ||
                        (quote.status && quote.status.toLowerCase().includes(searchLower))
                      );
                      
                      return orderMatches || quoteMatches;
                    }).map(order => (
                      <div key={order.id} className="border-b border-gray-100 last:border-b-0">
                        <div className="p-3 bg-gray-50">
                          <h4 className="font-medium">{order.orderTitle}</h4>
              </div>
                        {allOrderQuotesForUpload[order.id]?.map(quote => (
                          <div key={quote.id} className="p-3 flex items-center justify-between">
                            <div>
                              <span className="font-medium">{quote.supplier}</span>
                              <span className="text-gray-600 ml-2">
                                {formatPrice(quote.price, quote.currency)}
                        </span>
                      </div>
                            <input
                              type="checkbox"
                              checked={selectedQuotes.includes(`${order.id}:${quote.id}`)}
                              onChange={() => handleQuoteSelect(order.id, quote.id)}
                              className="h-4 w-4 text-black"
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleUpload}
                    disabled={uploading || selectedQuotes.length === 0}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                                  <button
                    onClick={() => setShowUploadSection(false)}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                                  </button>
                                </div>
                              </div>
            )}
                              </div>
                            </div>
      )}

        {loading ? (
        <div className="flex flex-col items-center justify-center p-8">
          <Loader2 size={32} className="animate-spin text-black" />
          <span className="mt-2">Loading vendors...</span>
          {loadingProgress.total > 0 && (
            <div className="mt-4 w-full max-w-md">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-black h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-1 text-center">
                Processing {loadingProgress.current} of {loadingProgress.total} batches
              </p>
            </div>
          )}
          </div>
        ) : (
          <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Found {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''} with PIs
                      </div>

          {filteredVendors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No vendors with PIs found</p>
              </div>
            ) : (
            <div className="grid gap-4">
              {filteredVendors.map((vendor) => (
                  <div 
                  key={vendor.name} 
                  onClick={() => handleVendorClick(vendor)}
                  className="bg-white rounded-lg shadow-md border border-gray-200 p-4 cursor-pointer hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-semibold text-lg">{vendor.name}</h3>
                        <p className="text-sm text-gray-600">
                          {vendor.piCount} PI{vendor.piCount !== 1 ? 's' : ''} • {vendor.totalOrders} order{vendor.totalOrders !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Latest PI</div>
                      <div className="font-medium">{formatDate(vendor.latestPI)}</div>
                      <ChevronRight size={20} className="text-gray-400 ml-2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        )}
      </div>
  );

  // Render Vendor PIs View
  const renderVendorPIsView = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={handleBackToVendors}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
{selectedVendor?.name}
          </h1>
          <p className="text-gray-600">Proforma Invoices from this vendor</p>
        </div>
        <div className="ml-auto">
            <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
              placeholder="Search PIs..."
                value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
            />
                      </div>
                      </div>
                    </div>

      {loadingPIs ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 size={32} className="animate-spin text-black" />
          <span className="ml-2">Loading PIs...</span>
                    </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Found {filteredVendorPIs.length} PI{filteredVendorPIs.length !== 1 ? 's' : ''}
                  </div>

          {filteredVendorPIs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No PIs found for this vendor</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredVendorPIs.map((pi) => (
                <div 
                  key={pi.id} 
                  onClick={() => handlePIClick(pi)}
                  className="bg-white rounded-lg shadow-md border border-gray-200 p-4 cursor-pointer hover:shadow-lg transition-shadow hover:border-gray-400"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText size={20} className="text-black" />
                        <h3 className="font-semibold text-lg text-black hover:text-gray-700">{removePdfExtension(pi.piName)}</h3>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Orders:</span>
                          <div>{pi.orderCount} order{pi.orderCount !== 1 ? 's' : ''}</div>
                        </div>
                        <div>
                          <span className="font-medium">Price:</span>
                          <div>{formatPrice(pi.price, pi.currency)}</div>
                        </div>
                        <div>
                          <span className="font-medium">Upload Date:</span>
                          <div>{formatDate(pi.uploadDate)}</div>
                        </div>
            </div>
          </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(pi.proformaInvoiceUrl, '_blank');
                        }}
                        className="p-2 text-gray-600 hover:text-black hover:bg-gray-50 rounded"
                                    title="View PI"
                                  >
                        <Eye size={18} />
                      </button>
                      
                                  <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(pi);
                        }}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                                    title="Delete PI"
                                  >
                        <Trash2 size={18} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
          )}
                      </div>
      )}
          </div>
  );

  // Render Order Detail View
  const renderOrderDetailView = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={handleBackToPIOrders}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-black">
            <Package size={22} /> {selectedOrder?.orderTitle}
          </h1>
          <p className="text-gray-600">Order Details</p>
                                </div>
                                </div>

      {selectedOrder && (
        <div className="space-y-6">
          {/* Order Header */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Order Information</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Order ID:</span>
                    <span className="ml-2">{selectedOrder.serialNumber || selectedOrder.id}</span>
                              </div>
                  <div>
                    <span className="font-medium text-gray-600">Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      selectedOrder.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      selectedOrder.status === 'In Progress' ? 'bg-gray-100 text-gray-800' :
                      selectedOrder.status === 'Awaiting Requirements' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                      {selectedOrder.status}
                                </span>
                              </div>
                  <div>
                    <span className="font-medium text-gray-600">Created:</span>
                    <span className="ml-2">{formatDate(selectedOrder.createdAt)}</span>
                            </div>
                  {selectedOrder.quantity && (
                    <div>
                      <span className="font-medium text-gray-600">Quantity:</span>
                      <span className="ml-2">{selectedOrder.quantity} units</span>
                        </div>
                  )}
                      </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Creator</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Name:</span>
                    <span className="ml-2">{selectedOrder.creator?.displayName || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Role:</span>
                    <span className="ml-2">{selectedOrder.creator?.role || 'Employee'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Category</h3>
                <div className="space-y-2 text-sm">
                  {selectedOrder.isCategoryOrder ? (
                    <div>
                      <span className="font-medium text-gray-600">Category Order:</span>
                      <span className="ml-2 text-black">Yes</span>
                    </div>
                  ) : (
                    <div>
                      <span className="font-medium text-gray-600">Category Order:</span>
                      <span className="ml-2 text-gray-500">No</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Order Description */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="font-semibold text-lg text-gray-900 mb-4">Description</h3>
            <div className="text-gray-700 whitespace-pre-wrap">
              {selectedOrder.description || 'No description provided'}
            </div>
          </div>

          {/* AI Summary */}
          {selectedOrder.aiSummary && (
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">AI Summary</h3>
              <div className="text-gray-700 whitespace-pre-wrap">
                {selectedOrder.aiSummary}
              </div>
            </div>
          )}

          {/* Quotes Section */}
          {allOrderQuotes[selectedOrder.id] && allOrderQuotes[selectedOrder.id].length > 0 && (
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                Quotes ({allOrderQuotes[selectedOrder.id].length})
              </h3>
              <div className="space-y-4">
                {allOrderQuotes[selectedOrder.id].map((quote, index) => (
                  <div key={quote.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-lg">{quote.supplier}</h4>
                        <p className="text-sm text-gray-600">
                          Quote #{index + 1} • {formatDate(quote.createdAt)}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-green-600">
                          {formatPrice(quote.price, quote.currency)}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded ${
                          quote.status === 'Best Quote' ? 'bg-green-100 text-green-800' :
                          quote.status === 'Under Negotiation' ? 'bg-yellow-100 text-yellow-800' :
                          quote.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {quote.status}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {quote.quantity && (
                        <div>
                          <span className="font-medium text-gray-600">Quantity:</span>
                          <span className="ml-2">{quote.quantity} units</span>
                        </div>
                      )}
                      {quote.deliveryTimeline && (
                        <div>
                          <span className="font-medium text-gray-600">Delivery:</span>
                          <span className="ml-2">{quote.deliveryTimeline} {quote.deliveryTimelineUnit || 'days'}</span>
                        </div>
                      )}
                      {quote.advancePayment && (
                        <div>
                          <span className="font-medium text-gray-600">Advance Payment:</span>
                          <span className="ml-2">{quote.advancePayment}%</span>
                        </div>
                      )}
                      {quote.paymentTerms && (
                        <div>
                          <span className="font-medium text-gray-600">Payment Terms:</span>
                          <span className="ml-2">{quote.paymentTerms}</span>
          </div>
        )}
      </div>

                    {quote.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="font-medium text-gray-600 text-sm">Notes:</span>
                        <p className="text-sm text-gray-700 mt-1">{quote.notes}</p>
              </div>
                    )}

                    {quote.proformaInvoiceUrl && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <a
                          href={quote.proformaInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-black hover:text-gray-700 text-sm"
                        >
                          <FileText size={16} />
                          View Proforma Invoice
                        </a>
                      </div>
                    )}
                    </div>
                  ))}
              </div>
                </div>
              )}
            </div>
      )}
          </div>
  );

  // Render PI Orders View
  const renderPIOrdersView = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
                    <button
          onClick={handleBackToVendorPIs}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
                    >
          <ArrowLeft size={20} />
                    </button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText size={22} /> {removePdfExtension(selectedPI?.piName)}
          </h1>
          <p className="text-gray-600">Orders using this Proforma Invoice</p>
                  </div>
      </div>

      {selectedPI && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-2">PI Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-800">Vendor:</span>
              <div className="text-gray-700">{selectedPI.supplier}</div>
            </div>
            <div>
              <span className="font-medium text-gray-800">Price:</span>
              <div className="text-gray-700">{formatPrice(selectedPI.price, selectedPI.currency)}</div>
            </div>
            <div>
              <span className="font-medium text-gray-800">Status:</span>
              <div className="text-gray-700">{selectedPI.status}</div>
            </div>
            <div>
              <span className="font-medium text-gray-800">Upload Date:</span>
              <div className="text-gray-700">{formatDate(selectedPI.uploadDate)}</div>
            </div>
              </div>
            </div>
          )}

      {loadingOrders ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 size={32} className="animate-spin text-black" />
          <span className="ml-2">Loading orders...</span>
          </div>
      ) : (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No orders found for this PI</p>
            </div>
          ) : (
            orders.map((order) => (
              <div 
                key={order.id} 
                onClick={() => handleOrderClick(order)}
                className="bg-white rounded-lg shadow-md border border-gray-200 p-4 cursor-pointer hover:shadow-lg transition-shadow hover:border-gray-400"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg text-black hover:text-gray-700">{order.orderTitle}</h3>
                    <p className="text-sm text-gray-600">Order ID: {order.serialNumber || order.id}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Status</div>
                    <div className="font-medium">{order.status}</div>
                    <div className="text-xs text-gray-600 mt-1">Click to view details</div>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-4">
                  <div className="font-medium">Description:</div>
                  <div className="mt-1 line-clamp-2">{order.description}</div>
                </div>

                {/* Show quotes for this order */}
                {allOrderQuotes[order.id] && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Quotes ({allOrderQuotes[order.id].length})</h4>
                    <div className="space-y-2">
                      {allOrderQuotes[order.id].slice(0, 3).map((quote) => (
                        <div key={quote.id} className="bg-gray-50 rounded p-3 text-sm">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-medium">{quote.supplier}</span>
                              <span className="text-gray-600 ml-2">
                                {formatPrice(quote.price, quote.currency)}
                              </span>
                            </div>
                            <div className="text-gray-600">{quote.status}</div>
                          </div>
                        </div>
                      ))}
                      {allOrderQuotes[order.id].length > 3 && (
                        <div className="text-xs text-gray-500 text-center py-1">
                          +{allOrderQuotes[order.id].length - 3} more quotes
                        </div>
                      )}
        </div>
      </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Proforma Invoice</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete <span className="font-semibold">"{removePdfExtension(piToDelete?.piName)}"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This will permanently remove the PI and all associated data.
                </p>
              </div>
              
            <div className="flex gap-3 justify-end">
              <button
                  onClick={cancelDelete}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md transition-colors"
                >
                  Delete
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-8 max-w-6xl mx-auto">
        {currentView === 'vendor-list' && renderVendorListView()}
        {currentView === 'vendor-pis' && renderVendorPIsView()}
        {currentView === 'pi-orders' && renderPIOrdersView()}
        {currentView === 'order-detail' && renderOrderDetailView()}
    </div>
    </>
  );
}